import React, { useState, useEffect, useRef } from 'react';
import PointSelection from './PointSelection/index.js';
import { getCustomerPoints } from '../../helpers/customer.js';
import { usePrevious } from '../../helpers/use-previous.js';
import { debounce } from 'diskho';
import { useSendMessage } from 'crustate/react';
import { useTranslate } from '@awardit/react-use-translate';
import { syncQuote } from '@crossroads/shop-state/quote';
import { Foldable, Button, Wrapper, Spinner } from '@crossroads/ui-components';
import { quotePointsSetToMaximum, quotePointsSet } from '../../queries/altapay.graphql.js';
import styles from 'shop-views/PartPayment.scss';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

var LoadingView = () => {
  return /*#__PURE__*/React.createElement("div", {
    className: styles.loadingView
  }, /*#__PURE__*/React.createElement(Wrapper, {
    className: styles.block
  }, /*#__PURE__*/React.createElement(Spinner, {
    variant: "primary"
  })));
};

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

var PartPayment = _ref => {
  function _temp(_ref4) {
    var quote = _ref4.quotePointsSetToMaximum.quote;

    if (quote) {
      sendMessage(syncQuote(quote));
      setIsLoading(false);
    }
  }

  var quoteData = _ref.quoteData,
      pointPaymentId = _ref.pointPaymentId,
      customerData = _ref.customerData,
      useClient = _ref.useClient,
      PointsTooltip = _ref.PointsTooltip,
      formatPrice = _ref.formatPrice,
      formatPoints = _ref.formatPoints,
      isEditingPoints = _ref.isEditingPoints,
      setEditingPoints = _ref.setEditingPoints,
      vatToUse = _ref.vatToUse;
  var sendMessage = useSendMessage();
  var customer = customerData ? customerData.data : null;
  var selectedPointPayment = quoteData.data.selectedPointPayment;
  var balance = getCustomerPoints(customer, pointPaymentId);
  var previousSelectedPointPayment = usePrevious(selectedPointPayment);
  var selectedPoints = (selectedPointPayment == null ? void 0 : selectedPointPayment.points.selected[vatToUse]) || 0;

  var _useState = useState(selectedPoints),
      sliderValue = _useState[0],
      setSliderValueReal = _useState[1];

  var _useState2 = useState(false),
      isLoading = _useState2[0],
      setIsLoading = _useState2[1];

  var client = useClient();
  var t = useTranslate();
  useEffect(() => {
    if (quoteData.state === "LOADED" && selectedPointPayment === null) {
      setIsLoading(true);

      _async(function () {
        return _await(client(quotePointsSetToMaximum, {
          id: pointPaymentId
        }), _temp);
      })();
    }
  }, [quoteData, selectedPointPayment]);
  useEffect(() => {
    if (selectedPointPayment && !previousSelectedPointPayment) {
      setSliderValueReal(selectedPointPayment.points.selected[vatToUse]);
    }
  }, [selectedPointPayment]);

  var setPointsPayment = _async(function (value) {
    var _ref2;

    var params = {
      id: pointPaymentId,
      points: value,
      incVat: (_ref2 = vatToUse === "incVat") != null ? _ref2 : false
    };
    var query = value >= 0 ? quotePointsSet : quotePointsSetToMaximum;
    var queryParams = value >= 0 ? params : {
      id: pointPaymentId
    };
    return _await(client(query, queryParams), function (_ref3) {
      var quote = _ref3[query === quotePointsSet ? "quotePointsSet" : "quotePointsSetToMaximum"].quote;

      if (quote) {
        sendMessage(syncQuote(quote));
        setIsLoading(false);
      }
    });
  });

  var setQuotePointsDebounced = useRef(debounce(x => {
    setPointsPayment(x);
  }, 100)).current;

  if (!selectedPointPayment || quoteData.state !== "LOADED") {
    return /*#__PURE__*/React.createElement(LoadingView, null);
  }

  var minimumPoints = selectedPointPayment.points.min[vatToUse];
  var maximumPoints = Math.min(selectedPointPayment.points.max[vatToUse], balance);
  var remainingCash = selectedPointPayment.currency.remaining.incVat;

  var limitPoints = p => {
    if (Number.isNaN(p)) {
      return selectedPoints;
    }

    if (p < minimumPoints) {
      return minimumPoints;
    }

    if (p > maximumPoints) {
      return maximumPoints;
    }

    return Math.max(minimumPoints, Math.min(maximumPoints, p));
  };

  var setSliderValue = v => {
    setIsLoading(true);
    var value = limitPoints(v);
    setSliderValueReal(value);
    setQuotePointsDebounced(value);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.box
  }, /*#__PURE__*/React.createElement(Foldable, {
    open: isEditingPoints
  }, /*#__PURE__*/React.createElement(PointSelection, {
    balance: balance,
    points: sliderValue,
    setPoints: setSliderValue,
    minValue: minimumPoints,
    maxValue: maximumPoints,
    remainingPrice: remainingCash,
    PointsTooltip: PointsTooltip,
    formatPrice: formatPrice,
    formatPoints: formatPoints,
    isProcessing: isLoading
  }), /*#__PURE__*/React.createElement(Button, {
    className: styles.submitButton,
    variant: "primary",
    loading: isLoading,
    onClick: () => setEditingPoints(false)
  }, t("CHECKOUT.CONTINUE"))), /*#__PURE__*/React.createElement(Foldable, {
    className: styles.summary,
    open: !isEditingPoints
  }, /*#__PURE__*/React.createElement("p", null, t("CHECKOUT.PAYMENT_DISTRIBUTION"), ":", /*#__PURE__*/React.createElement("br", null), formatPoints && formatPoints(sliderValue), remainingCash !== undefined && remainingCash > 0 && /*#__PURE__*/React.createElement("span", {
    className: styles.remainder
  }, " + ", formatPrice(remainingCash)))));
};

export { PartPayment };
//# sourceMappingURL=index.js.map
